// JWT Service with proper signature validation and security features

import type { JWTPayload, JWTVerifyOptions } from 'jose'
import { createRemoteJWKSet, jwtVerify } from 'jose'
import { criticalLogger, logger } from '../../utils/logger'

import { AuthentikAuthError } from '../../types/auth'

interface JWTValidationResult {
  valid: boolean
  payload?: JWTPayload
  error?: string
  expiresAt?: number
}

interface JWKSCache {
  [issuer: string]: {
    jwks: ReturnType<typeof createRemoteJWKSet>
    lastUpdated: number
    ttl: number
  }
}

class JWTService {
  private jwksCache: JWKSCache = {}
  private readonly JWKS_CACHE_TTL = 60 * 60 * 1000 // 1 hour
  private readonly VALIDATION_TIMEOUT = 10000 // 10 seconds

  /**
   * Validate JWT token with signature verification
   */
  async validateToken(
    token: string,
    issuer: string,
    audience: string,
    jwksEndpoint?: string
  ): Promise<JWTValidationResult> {
    try {
      // Basic format validation
      if (!this.isValidTokenFormat(token)) {
        return { valid: false, error: 'Invalid JWT token format' }
      }

      // Get or create JWKS client
      const jwks = await this.getJWKS(issuer, jwksEndpoint)

      // Verification options
      const options: JWTVerifyOptions = {
        issuer,
        audience,
        clockTolerance: 30, // 30 seconds tolerance for clock skew
        maxTokenAge: '24h' // Maximum token age
      }

      // Add timeout to prevent hanging
      const verificationPromise = jwtVerify(token, jwks, options)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('JWT validation timeout')), this.VALIDATION_TIMEOUT)
      })

      const { payload } = await Promise.race([verificationPromise, timeoutPromise])

      logger.info('JWT validation successful', {
        subject: payload.sub,
        expiresAt: payload.exp,
        issuedAt: payload.iat
      })

      return {
        valid: true,
        payload,
        expiresAt: payload.exp ? payload.exp * 1000 : undefined
      }
    } catch (error) {
      const errorMessage = this.categorizeJWTError(error)

      criticalLogger.error('JWT validation failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        issuer,
        audience
      })

      return { valid: false, error: errorMessage }
    }
  }

  /**
   * Validate multiple tokens (for batch processing)
   */
  async validateTokens(
    tokens: { token: string; issuer: string; audience: string }[]
  ): Promise<JWTValidationResult[]> {
    const validationPromises = tokens.map(({ token, issuer, audience }) =>
      this.validateToken(token, issuer, audience)
    )

    return Promise.all(validationPromises)
  }

  /**
   * Extract claims without validation (for debugging only)
   */
  extractClaimsUnsafe(token: string): JWTPayload {
    logger.warn('🚨 Extracting JWT claims without signature validation - FOR DEBUGGING ONLY')

    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format')
      }

      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decoded) as JWTPayload
    } catch (error) {
      throw new AuthentikAuthError('Failed to decode JWT token', error)
    }
  }

  /**
   * Check if token is expired without full validation
   */
  isTokenExpired(token: string): boolean {
    try {
      const claims = this.extractClaimsUnsafe(token)
      if (!claims.exp) return false

      return Date.now() >= claims.exp * 1000
    } catch {
      return true // Assume expired if can't decode
    }
  }

  /**
   * Get time until token expires
   */
  getTimeUntilExpiry(token: string): number | null {
    try {
      const claims = this.extractClaimsUnsafe(token)
      if (!claims.exp) return null

      const expiryTime = claims.exp * 1000
      const timeUntilExpiry = expiryTime - Date.now()

      return timeUntilExpiry > 0 ? timeUntilExpiry : 0
    } catch {
      return null
    }
  }

  /**
   * Clear JWKS cache (useful for testing or cache invalidation)
   */
  clearJWKSCache(): void {
    this.jwksCache = {}
    logger.info('JWKS cache cleared')
  }

  // Private helper methods

  private isValidTokenFormat(token: string): boolean {
    if (!token || typeof token !== 'string') return false

    const parts = token.split('.')
    if (parts.length !== 3) return false

    // Basic check for valid base64url encoding
    const base64UrlRegex = /^[A-Za-z0-9_-]+$/
    return parts.every((part) => base64UrlRegex.test(part))
  }

  private async getJWKS(
    issuer: string,
    jwksEndpoint?: string
  ): Promise<ReturnType<typeof createRemoteJWKSet>> {
    const cacheKey = issuer
    const now = Date.now()

    // Check cache
    if (
      this.jwksCache[cacheKey] &&
      now - this.jwksCache[cacheKey].lastUpdated < this.JWKS_CACHE_TTL
    ) {
      return this.jwksCache[cacheKey].jwks
    }

    // Create new JWKS client
    const jwksUrl = jwksEndpoint || `${issuer}/.well-known/jwks.json`
    const jwks = createRemoteJWKSet(new URL(jwksUrl), {
      timeoutDuration: 5000, // 5 second timeout
      cooldownDuration: 30000 // 30 second cooldown between requests
    })

    // Cache the JWKS client
    this.jwksCache[cacheKey] = {
      jwks,
      lastUpdated: now,
      ttl: this.JWKS_CACHE_TTL
    }

    logger.info('JWKS client created and cached', { issuer, jwksUrl })
    return jwks
  }

  private categorizeJWTError(error: unknown): string {
    if (!(error instanceof Error)) {
      return 'Unknown JWT validation error'
    }

    const message = error.message.toLowerCase()

    if (message.includes('fetch') || message.includes('network')) {
      return 'Unable to fetch JWT verification keys - check network connection'
    }
    if (message.includes('signature')) {
      return 'JWT signature verification failed - token may be forged'
    }
    if (message.includes('expired') || message.includes('exp')) {
      return 'JWT token has expired - please sign in again'
    }
    if (message.includes('issuer') || message.includes('iss')) {
      return 'JWT issuer validation failed - token from unauthorized source'
    }
    if (message.includes('audience') || message.includes('aud')) {
      return 'JWT audience validation failed - token for different application'
    }
    if (message.includes('timeout')) {
      return 'JWT validation timeout - please try again'
    }
    if (message.includes('algorithm') || message.includes('alg')) {
      return 'Unsupported or insecure JWT algorithm'
    }

    return 'JWT token validation failed'
  }
}

// Export singleton instance
export const jwtService = new JWTService()

// Export types
export type { JWTValidationResult }
